import { useCallback, useLayoutEffect, useState } from 'react';
import type { RefCallback, RefObject } from 'react';

import { TooltipAlign, type TooltipAlignType, TooltipPosition, type TooltipPositionType } from './types';

interface Position {
    x: number;
    y: number;
}

interface UseTooltipPositionOptions {
    align?: TooltipAlignType;
    side: TooltipPositionType;
    sideOffset?: number;
    triggerRef: RefObject<HTMLElement | null>;
}

interface UseTooltipPositionResult {
    actualSide: TooltipPositionType;
    contentRef: RefCallback<HTMLElement>;
    position: Position;
}

const getAlignmentOffset = (triggerSize: number, contentSize: number, align: TooltipAlignType): number => {
    switch (align) {
        case TooltipAlign.START:
            return 0;
        case TooltipAlign.END:
            return triggerSize - contentSize;
        case TooltipAlign.CENTER:
        default:
            return (triggerSize - contentSize) / 2;
    }
};

const calculatePosition = (
    triggerRect: DOMRect,
    contentRect: DOMRect,
    side: TooltipPositionType,
    align: TooltipAlignType,
    sideOffset: number,
): Position => {
    let x = 0;
    let y = 0;

    switch (side) {
        case TooltipPosition.TOP:
            x = triggerRect.left + getAlignmentOffset(triggerRect.width, contentRect.width, align);
            y = triggerRect.top - contentRect.height - sideOffset;
            break;
        case TooltipPosition.BOTTOM:
            x = triggerRect.left + getAlignmentOffset(triggerRect.width, contentRect.width, align);
            y = triggerRect.bottom + sideOffset;
            break;
        case TooltipPosition.LEFT:
            x = triggerRect.left - contentRect.width - sideOffset;
            y = triggerRect.top + getAlignmentOffset(triggerRect.height, contentRect.height, align);
            break;
        case TooltipPosition.RIGHT:
        default:
            x = triggerRect.right + sideOffset;
            y = triggerRect.top + getAlignmentOffset(triggerRect.height, contentRect.height, align);
            break;
    }

    return { x, y };
};

const OPPOSITE_SIDE: Record<TooltipPositionType, TooltipPositionType> = {
    [TooltipPosition.BOTTOM]: TooltipPosition.TOP,
    [TooltipPosition.LEFT]: TooltipPosition.RIGHT,
    [TooltipPosition.RIGHT]: TooltipPosition.LEFT,
    [TooltipPosition.TOP]: TooltipPosition.BOTTOM,
};

const wouldOverflow = (position: Position, contentRect: DOMRect, side: TooltipPositionType): boolean => {
    const viewport = { height: window.innerHeight, width: window.innerWidth };

    switch (side) {
        case TooltipPosition.TOP:
            return position.y < 0;
        case TooltipPosition.BOTTOM:
            return position.y + contentRect.height > viewport.height;
        case TooltipPosition.LEFT:
            return position.x < 0;
        case TooltipPosition.RIGHT:
            return position.x + contentRect.width > viewport.width;
        default:
            return false;
    }
};

export const useTooltipPosition = ({
    align = TooltipAlign.CENTER,
    side,
    sideOffset = 0,
    triggerRef,
}: UseTooltipPositionOptions): UseTooltipPositionResult => {
    const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
    const [actualSide, setActualSide] = useState<TooltipPositionType>(side);
    const [contentElement, setContentElement] = useState<HTMLElement | null>(null);

    const contentRef: RefCallback<HTMLElement> = useCallback((node: HTMLElement | null) => {
        setContentElement(node);
    }, []);

    const updatePosition = useCallback(() => {
        const trigger = triggerRef.current;

        if (!trigger || !contentElement) {
            return;
        }

        const triggerRect = trigger.getBoundingClientRect();
        const contentRect = contentElement.getBoundingClientRect();

        let calculatedPosition = calculatePosition(triggerRect, contentRect, side, align, sideOffset);
        let finalSide = side;

        if (wouldOverflow(calculatedPosition, contentRect, side)) {
            const oppositeSide = OPPOSITE_SIDE[side];
            const oppositePosition = calculatePosition(triggerRect, contentRect, oppositeSide, align, sideOffset);

            if (!wouldOverflow(oppositePosition, contentRect, oppositeSide)) {
                calculatedPosition = oppositePosition;
                finalSide = oppositeSide;
            }
        }

        const VIEWPORT_PADDING = 8;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        calculatedPosition.x = Math.max(
            VIEWPORT_PADDING,
            Math.min(calculatedPosition.x, viewportWidth - contentRect.width - VIEWPORT_PADDING),
        );
        calculatedPosition.y = Math.max(
            VIEWPORT_PADDING,
            Math.min(calculatedPosition.y, viewportHeight - contentRect.height - VIEWPORT_PADDING),
        );

        calculatedPosition.x += window.scrollX;
        calculatedPosition.y += window.scrollY;

        setPosition(calculatedPosition);
        setActualSide(finalSide);
    }, [align, contentElement, side, sideOffset, triggerRef]);

    useLayoutEffect(() => {
        updatePosition();

        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);

        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [updatePosition]);

    useLayoutEffect(() => {
        if (!contentElement) {
            return undefined;
        }

        const resizeObserver = new ResizeObserver(() => {
            updatePosition();
        });

        resizeObserver.observe(contentElement);

        return () => {
            resizeObserver.disconnect();
        };
    }, [contentElement, updatePosition]);

    return { actualSide, contentRef, position };
};
